import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the LeaveBalanceContext
const LeaveBalanceContext = createContext();

// Custom hook to use the LeaveBalanceContext
export const useLeaveBalance = () => useContext(LeaveBalanceContext);

// Provider component
export const LeaveBalanceProvider = ({ children }) => {
  const [leaveBalances, setLeaveBalance] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_URL = "https://hrmsnew.prepstripe.com/api/leave-details"; // Replace with your actual API URL
  const token = getToken();

  const fetchLeaveBalances = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      await new Promise((r) => setTimeout(r, 1000));

      // setLeaveBalance(response.data?.data?.data || []); // Assuming leave balances are inside `data.data.data`
      setLeaveBalance([...(response.data?.data?.data || [])].reverse());
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
      setError(null);
    } catch (error) {
      console.error("Error fetching leave balance:", error);
      setError(error.response?.data?.message || "Failed to fetch leave balance.");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchLeaveBalances();
  }, [API_URL, token]); // Re-fetch only if API_URL or token changes

  // Add a new interview
  const addLeaveBalance = async (newLeaveBalance) => {
    // console.log("new leave balance payload: ", newLeaveBalance)
    try {
      const response = await axios.post(API_URL, newLeaveBalance, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setLeaveBalance((prev) => [...prev, response.data?.data]);
      fetchLeaveBalances(); // Refresh the leave balances after adding a new one
      return { success: true };
    } catch (error) {
      console.error("Error adding leave balance:", error);
      setError(error.response?.data?.message || "Failed to add leave balance.");
      return { success: false, message: error.response?.data };
    }
  };

  // Update an existing leave balance
  const updateLeaveBalance = async (updatedLeaveBalance) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedLeaveBalance.id}`,
        updatedLeaveBalance,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // setLeaveBalance((prev) =>
      //   prev.map((leaveBalance) =>
      //     leaveBalance.id === updatedLeaveBalance.id ? response.data?.data : leaveBalance
      //   )
      // );
      fetchLeaveBalances(); // Refresh the leave balances after updating
      return { success: true };
    } catch (error) {
      console.error("Error updating leave balance:", error);
      setError(error.response?.data?.message || "Failed to update leave balance.");
      return { success: false, message: error.response?.data };
    }
  };

  // Delete an leave balance
  const deleteLeaveBalance = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLeaveBalance((prev) => prev.filter((leaveBalance) => leaveBalance.id !== id));
      return { success: true };
    } catch (error) {
      console.error("Error deleting leave balance:", error);
      setError(error.response?.data?.message || "Failed to delete leave balance.");
      return { success: false, message: error.response?.data };
    }
  };


  const handleBulkLeaveCreate = async () => {
    try {
      const response = await axios.post(
        "https://hrmsnew.prepstripe.com/api/leave-details/bulk-create",
        {}, // Pass your bulk leave payload here instead of {}
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response?.data?.success) {
        // setLeaveBalance((prev) => [...prev, response.data?.data]);
        fetchLeaveBalances(); // Refresh the leave balances after bulk creation
        return { success: true };
      } else {
        // console.log(`Error: ${response?.data?.message || "Failed to create bulk leave balance."}`);
      }
    } catch (error) {
      console.error("Bulk create error:", error);
      return { success: false, message: error.response?.data };
    }
  };
    

  // Memoize the context value
  const value = useMemo(
    () => ({
      leaveBalances,
      pagination,
      loading,
      error,
      fetchLeaveBalances,
      addLeaveBalance,
      handleBulkLeaveCreate,
      updateLeaveBalance,
      deleteLeaveBalance,
    }),
    [leaveBalances, loading, error] // Recalculate the value only when these dependencies change
  );

  return <LeaveBalanceContext.Provider value={value}>{children}</LeaveBalanceContext.Provider>;
};
